import styles from './note-panel.module.css';

type NoteTone = 'accent' | 'warn' | 'danger';

interface NotePanelProps {
  /** Uppercase eyebrow label (e.g. "Why this variant", "Chef's Note"). */
  label: string;
  children: React.ReactNode;
  /**
   * Semantic colour of the rule and label. `accent` (default) is editorial —
   * a note worth reading. `warn` and `danger` are for states the user may need
   * to act on, e.g. a stale shopping list or a failed recalculation.
   */
  tone?: NoteTone;
  /** Italicises the body — used for the chef's-note technique insight. */
  italic?: boolean;
  /**
   * Announce as a live region. Off by default — most NotePanels are static
   * editorial content that a screen reader should reach in document order, not
   * interrupt for. Set it when the panel is inserted in response to a user
   * action (a failed recalc, a stale-list warning) and would otherwise be
   * silent. `danger` announces assertively, others politely.
   */
  alert?: boolean;
  /** Optional trailing action (e.g. a "Recalculate now" Button). */
  action?: React.ReactNode;
  /** Accessible name for the surrounding <aside> region. */
  ariaLabel?: string;
  id?: string;
  className?: string;
}

/**
 * Accent-left-border callout used across the recipe detail page for both the
 * "Why this variant" note and the "Chef's note" technique insight. Defined once
 * here (§13) so those two surfaces compose a single element rather than each
 * redrawing the same border/label/body treatment.
 */
const TONE_CLASS: Record<NoteTone, string | undefined> = {
  accent: undefined,
  warn: styles.toneWarn,
  danger: styles.toneDanger,
};

export function NotePanel({
  label,
  children,
  tone = 'accent',
  italic = false,
  alert = false,
  action,
  ariaLabel,
  id,
  className,
}: NotePanelProps) {
  const classes = [styles.panel, TONE_CLASS[tone], className].filter(Boolean).join(' ');
  const liveProps = alert
    ? ({
        role: tone === 'danger' ? 'alert' : 'status',
        'aria-live': tone === 'danger' ? 'assertive' : 'polite',
      } as const)
    : {};
  return (
    <aside id={id} className={classes} aria-label={ariaLabel} {...liveProps}>
      <p className={styles.label}>{label}</p>
      <p className={italic ? `${styles.text} ${styles.italic}` : styles.text}>{children}</p>
      {action ? <div className={styles.action}>{action}</div> : null}
    </aside>
  );
}
